import { LitElement, html, css } from 'lit';

export class UITransfer extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: String, reflect: true }, // Comma-separated target values
    options: { type: Array }, // All available options: { value, label, disabled }
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    titles: { type: Array }, // e.g. ['Source', 'Target']
    label: { type: String, reflect: true },
    
    // Internal States
    targetValues: { type: Array, state: true }, // Values currently in the target column
    sourceSelected: { type: Array, state: true }, // Selected values in source ready to move
    targetSelected: { type: Array, state: true }, // Selected values in target ready to move back
    sourceSearch: { type: String, state: true },
    targetSearch: { type: String, state: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .transfer-container {
      display: flex;
      align-items: stretch;
      gap: 16px;
      width: 100%;
      box-sizing: border-box;
    }

    /* List Column Panel */
    .transfer-column {
      flex: 1;
      display: flex;
      flex-direction: column;
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-lg);
      background-color: var(--ui-surface);
      overflow: hidden;
      min-height: 260px;
      max-height: 320px;
      box-shadow: var(--ui-shadow-sm);
      transition: var(--ui-transition-fast);
      box-sizing: border-box;
    }

    .transfer-column:hover:not(.disabled) {
      border-color: var(--ui-border-hover);
    }

    .transfer-column.disabled {
      opacity: 0.6;
      background-color: var(--ui-surface-active);
    }

    /* Column Header */
    .column-header {
      padding: 10px 14px;
      background-color: var(--ui-surface-hover);
      border-bottom: 1px solid var(--ui-border);
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-weight: 600;
      font-size: var(--ui-font-size-base);
    }

    .column-count {
      font-size: var(--ui-font-size-sm);
      color: var(--ui-text-muted);
      font-weight: normal;
    }

    /* Search row */
    .search-wrapper {
      padding: 8px 10px;
      border-bottom: 1px solid var(--ui-border);
    }

    .search-input {
      width: 100%;
      height: 32px;
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-sm);
      background-color: var(--ui-surface);
      color: var(--ui-text);
      padding: 0 10px;
      font-family: inherit;
      font-size: var(--ui-font-size-sm);
      outline: none;
      box-sizing: border-box;
      transition: var(--ui-transition-fast);
    }

    .search-input:focus {
      border-color: var(--ui-primary);
    }

    /* List content scrollable */
    .items-list {
      flex: 1;
      overflow-y: auto;
      padding: 6px;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .list-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      border-radius: var(--ui-radius-sm);
      cursor: pointer;
      user-select: none;
      transition: var(--ui-transition-fast);
      font-size: var(--ui-font-size-sm);
    }

    .list-item:hover:not(.item-disabled) {
      background-color: var(--ui-surface-hover);
    }

    .list-item.selected {
      background-color: var(--ui-surface-active);
    }

    .list-item.item-disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .list-item-checkbox {
      accent-color: var(--ui-primary);
      width: 14px;
      height: 14px;
      cursor: pointer;
    }

    .list-item-label {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .empty-state {
      padding: 24px;
      text-align: center;
      color: var(--ui-text-muted);
      font-size: var(--ui-font-size-sm);
      margin: auto;
    }

    /* Buttons Column middle */
    .transfer-controls {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 10px;
      padding: 0 4px;
    }

    .control-btn {
      width: 36px;
      height: 36px;
      border: 1px solid var(--ui-border);
      background-color: var(--ui-surface);
      color: var(--ui-text-muted);
      border-radius: var(--ui-radius-md);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: var(--ui-transition-fast);
      box-shadow: var(--ui-shadow-sm);
    }

    .control-btn:hover:not(:disabled) {
      background-color: var(--ui-primary);
      border-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
      transform: scale(1.05);
    }

    .control-btn:disabled {
      cursor: not-allowed;
      opacity: 0.5;
      box-shadow: none;
    }

    @media (max-width: 576px) {
      .transfer-container {
        flex-direction: column;
        gap: 12px;
      }

      .transfer-controls {
        flex-direction: row;
        justify-content: center;
      }
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = '';
    this.options = [];
    this.disabled = false;
    this.required = false;
    this.titles = ['Source', 'Cible'];
    this.label = '';

    this.targetValues = [];
    this.sourceSelected = [];
    this.targetSelected = [];
    this.sourceSearch = '';
    this.targetSearch = '';
  }

  willUpdate(changedProperties) {
    if (typeof this.options === 'string') {
      try {
        this.options = JSON.parse(this.options);
      } catch (e) {
        this.options = [];
      }
    }

    if (typeof this.titles === 'string') {
      try {
        this.titles = JSON.parse(this.titles);
      } catch (e) {
        this.titles = ['Source', 'Cible'];
      }
    }

    if (changedProperties.has('value')) {
      this._syncTargetValuesFromValue();
    }
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      this.internals.setFormValue(this.value);
      this._updateValidity();
    }
  }

  _updateValidity() {
    const hasItems = this.targetValues && this.targetValues.length > 0;
    if (this.required && !hasItems) {
      this.internals.setValidity(
        { valueMissing: true },
        'Veuillez transférer au moins un élément vers la cible.',
        this.shadowRoot.querySelector('.transfer-column')
      );
    } else {
      this.internals.setValidity({});
    }
  }

  _syncTargetValuesFromValue() {
    if (this.value) {
      this.targetValues = this.value.split(',').map(v => v.trim()).filter(v => v !== '');
    } else {
      this.targetValues = [];
    }
  }

  _syncValueFromTargetValues() {
    const newVal = this.targetValues.join(',');
    if (newVal !== this.value) {
      this.value = newVal;
      this.dispatchEvent(new CustomEvent('change', {
        detail: { value: newVal },
        bubbles: true,
        composed: true
      }));
    }
  }

  _toggleSelectSource(val, isSelected) {
    if (this.disabled) return;
    if (isSelected) {
      this.sourceSelected = [...this.sourceSelected, val];
    } else {
      this.sourceSelected = this.sourceSelected.filter(v => v !== val);
    }
  }

  _toggleSelectTarget(val, isSelected) {
    if (this.disabled) return;
    if (isSelected) {
      this.targetSelected = [...this.targetSelected, val];
    } else {
      this.targetSelected = this.targetSelected.filter(v => v !== val);
    }
  }

  _moveToTarget() {
    if (this.disabled || this.sourceSelected.length === 0) return;
    
    // Add selected sources to targets
    this.targetValues = [...new Set([...this.targetValues, ...this.sourceSelected])];
    this.sourceSelected = [];
    this._syncValueFromTargetValues();
  }

  _moveToSource() {
    if (this.disabled || this.targetSelected.length === 0) return;

    // Remove selected targets from targets list
    this.targetValues = this.targetValues.filter(v => !this.targetSelected.includes(v));
    this.targetSelected = [];
    this._syncValueFromTargetValues();
  }

  _getSourceItems() {
    if (!Array.isArray(this.options)) return [];
    return this.options.filter(opt => !this.targetValues.includes(opt.value));
  }

  _getTargetItems() {
    if (!Array.isArray(this.options)) return [];
    // Retain initial option orders for transfer target list
    return this.options.filter(opt => this.targetValues.includes(opt.value));
  }

  render() {
    const sourceItems = this._getSourceItems();
    const targetItems = this._getTargetItems();

    const filteredSource = sourceItems.filter(item => 
      String(item.label || item.value).toLowerCase().includes(this.sourceSearch.toLowerCase())
    );

    const filteredTarget = targetItems.filter(item => 
      String(item.label || item.value).toLowerCase().includes(this.targetSearch.toLowerCase())
    );

    const titleSource = this.titles[0] || 'Source';
    const titleTarget = this.titles[1] || 'Cible';

    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="transfer-container" part="input">
        <!-- Left Panel: Source -->
        <div class="transfer-column ${this.disabled ? 'disabled' : ''}">
          <div class="column-header">
            <span>${titleSource}</span>
            <span class="column-count">${filteredSource.length} / ${sourceItems.length}</span>
          </div>
          <div class="search-wrapper">
            <input 
              type="text" 
              class="search-input" 
              placeholder="Rechercher..." 
              .value="${this.sourceSearch}"
              ?disabled="${this.disabled}"
              @input="${(e) => this.sourceSearch = e.target.value}"
            />
          </div>
          <div class="items-list">
            ${filteredSource.length > 0 ? filteredSource.map(item => {
              const isSelected = this.sourceSelected.includes(item.value);
              const isDisabled = item.disabled || this.disabled;
              return html`
                <div 
                  class="list-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'item-disabled' : ''}"
                  @click="${() => !isDisabled && this._toggleSelectSource(item.value, !isSelected)}"
                >
                  <input 
                    type="checkbox" 
                    class="list-item-checkbox" 
                    .checked="${isSelected}" 
                    ?disabled="${isDisabled}"
                    @click="${(e) => e.stopPropagation()}"
                    @change="${(e) => this._toggleSelectSource(item.value, e.target.checked)}"
                  />
                  <span class="list-item-label" title="${item.label}">${item.label}</span>
                </div>
              `;
            }) : html`<div class="empty-state">Vide</div>`}
          </div>
        </div>

        <!-- Arrows Panel -->
        <div class="transfer-controls">
          <button 
            type="button" 
            class="control-btn"
            ?disabled="${this.disabled || this.sourceSelected.length === 0}"
            @click="${this._moveToTarget}"
            title="Transférer à droite"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <button 
            type="button" 
            class="control-btn"
            ?disabled="${this.disabled || this.targetSelected.length === 0}"
            @click="${this._moveToSource}"
            title="Transférer à gauche"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
        </div>

        <!-- Right Panel: Target -->
        <div class="transfer-column ${this.disabled ? 'disabled' : ''}">
          <div class="column-header">
            <span>${titleTarget}</span>
            <span class="column-count">${filteredTarget.length} / ${targetItems.length}</span>
          </div>
          <div class="search-wrapper">
            <input 
              type="text" 
              class="search-input" 
              placeholder="Rechercher..." 
              .value="${this.targetSearch}"
              ?disabled="${this.disabled}"
              @input="${(e) => this.targetSearch = e.target.value}"
            />
          </div>
          <div class="items-list">
            ${filteredTarget.length > 0 ? filteredTarget.map(item => {
              const isSelected = this.targetSelected.includes(item.value);
              const isDisabled = item.disabled || this.disabled;
              return html`
                <div 
                  class="list-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'item-disabled' : ''}"
                  @click="${() => !isDisabled && this._toggleSelectTarget(item.value, !isSelected)}"
                >
                  <input 
                    type="checkbox" 
                    class="list-item-checkbox" 
                    .checked="${isSelected}" 
                    ?disabled="${isDisabled}"
                    @click="${(e) => e.stopPropagation()}"
                    @change="${(e) => this._toggleSelectTarget(item.value, e.target.checked)}"
                  />
                  <span class="list-item-label" title="${item.label}">${item.label}</span>
                </div>
              `;
            }) : html`<div class="empty-state">Vide</div>`}
          </div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('ui-transfer')) {
  customElements.define('ui-transfer', UITransfer);
}
